import { Plus } from "lucide-react";
import "@app/App.css";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@comps/ui/dialog";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@comps/ui/form";
import { Input } from "@comps/ui/input";
import { Button } from "@comps/ui/button";
import { post } from "@utils/utils";
import { useState } from "react";
import { useModelStore } from "src/store";
import { useTranslation } from "react-i18next";
import { BASE_URL, DEFAULT_ORDER_INCREMENT } from "@utils/constants";
import { getWsButtonStyle, TW_ErrorMessage, TW_NewViewButton, TW_NewViewButtonIcon, TW_NewViewButtonText, useHover } from "@utils/twClasses";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { translate } from "src/i18n/i18n";
import { Dashboard } from "@dto/Dashboard";

interface NewDashboardButtonProps {
  onNewDashboard: (dashboard: Dashboard) => void;
}

function NewDashboardButton({ onNewDashboard }: NewDashboardButtonProps) {

  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const wsColor = useModelStore(state => state.wsColor);
  const wsHoverColor = useModelStore(state => state.wsHoverColor);
  const buttonHover = useHover(getWsButtonStyle('transparent', null), getWsButtonStyle(wsHoverColor, wsColor))

  const dashboardAdded = (dashboard: Dashboard) => {
    onNewDashboard(dashboard);
    setIsDialogOpen(false);
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger className={TW_NewViewButton} {...buttonHover}>
        <Plus className={TW_NewViewButtonIcon} />
        <p className={TW_NewViewButtonText}>{t("dashboard.button.new.text")}</p>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("dashboard.dialog.new.title")}</DialogTitle>
          <DialogDescription className="hidden">{t("dashboard.dialog.new.description")}</DialogDescription>
        </DialogHeader>
        <NewDashboardForm onNewDashboard={dashboardAdded} />
      </DialogContent>
    </Dialog>
  )
}

function NewDashboardForm({ onNewDashboard }: { onNewDashboard: (dashboard: Dashboard) => void }) {

  const { t } = useTranslation();
  const workspace = useModelStore(state => state.selectedWorkspace);
  const dashboards = useModelStore(state => state.dashboards);
  const [error, setError] = useState<string | null>(null);

  const formSchema = z.object({
    dashboardName: z.string().min(1, {
      message: t("dashboard.dialog.error.name.missing"),
    }),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      dashboardName: t("dashboard.dialog.name.default.value"),
    },
  })

  function onSubmit(values: z.infer<typeof formSchema>) {
    if (!workspace) {
      console.error("No workspace selected");
      return;
    }
    let order = 0;
    if (dashboards && dashboards.length > 0) {
      const lastDashboard = dashboards[dashboards.length - 1];
      if (lastDashboard.order) {
        order = lastDashboard.order + DEFAULT_ORDER_INCREMENT;
      } else {
        order = DEFAULT_ORDER_INCREMENT;
      }
    }

    const body = {
      name: values.dashboardName,
      order: order,
    }
    post<Dashboard>(`${BASE_URL}/model/workspaces/${workspace._key}/dashboards`, body).then(newDashboard => {
      if (newDashboard) {
        if (isCompozzError(newDashboard)) {
          const cError = newDashboard as CompozzError;
          if (cError.i18n) {
            setError(translate(t, cError.i18n, cError.params));
          } else {
            setError(cError.message);
          }
        } else {          
          setError(null);
          onNewDashboard(newDashboard as Dashboard);
        }
      }
    })
  }

  function onEnterKeyPress(event: React.KeyboardEvent) {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      form.handleSubmit(onSubmit)();
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <FormField
          control={form.control}
          name="dashboardName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("dashboard.dialog.name.label")}</FormLabel>
              <FormControl>
                <Input placeholder={t("dashboard.dialog.name.placeholder")} {...field} onKeyDown={onEnterKeyPress} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {error && <div className={TW_ErrorMessage}>{error}</div>}
        {!error && <div className={TW_ErrorMessage}></div>}
        <div className="ButtonList">
          <Button className="AppDialogButton" type="submit">{t("general.button.ok.text")}</Button>
        </div>
      </form>
    </Form>
  )
}

export default NewDashboardButton;
